
import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  useScheme,
  useSchemeCycles,
  useSchemeSubscribers,
  useEnrollSubscriber,
  useAdmitSubscriber,
  useUpdateCycleAuctionDay,
} from '../../../../features/schemes/network';
import type { SchemeDetail, SubscriberListItem } from '../../../../features/schemes/network';
import { SchemeStatusActions } from '../../../../features/schemes/components/SchemeStatusActions';
import { useCustomers } from '../../../../features/customers/hooks';

import { ChevronLeft, Edit2 } from 'lucide-react';
import { StatusBadge } from '../../../../components/ui/StatusBadge';
import { Button } from '../../../../components/ui/Button';
import { Card, CardContent } from '../../../../components/ui/Card';
import { FormField, Input } from '../../../../components/ui/FormField';
import { Skeleton } from '../../../../components/ui/Skeleton';
import { useDebounce, usePageTitle } from '../../../../lib/hooks';
import { useState, useMemo } from 'react';
import { useToast } from '../../../../components/ui/Toast';
import { useFormModal } from '@repo/form';
import { FormModal } from '@repo/web-form';
import { Money } from "../../../../components/ui/Money";
import { defineDetailPage, useDetailPage } from '@repo/detail';
import { DetailPage } from '@repo/web-detail';
import Decimal from 'decimal.js';
import { z } from 'zod';
import { MemberDetailDrawer } from '../../../../features/schemes/components/MemberDetailDrawer';
import { MarkDeceasedDialog } from '../../../../features/schemes/components/MarkDeceasedDialog';
import { SchemeLedgerTab } from '../../../../features/schemes/components/SchemeLedgerTab';
import { SchemeDocumentsTab } from '../../../../features/schemes/components/SchemeDocumentsTab';
import { SchemeClosureTab } from '../../../../features/schemes/components/SchemeClosureTab';
import { DataView } from '../../../../components/ui/DataView';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../../components/ui/Dialog';

export const Route = createFileRoute('/_auth/schemes/$id/')({
 component: SchemeDetail,
});

type SchemeWithFields = SchemeDetail & {
  schemeNumber?: string;
  displayName?: string;
  productTypeId?: string;
  subscriberCount?: number;
  cycleFrequency?: 'MONTHLY' | 'FORTNIGHTLY' | 'WEEKLY';
  auctionType?: 'OPEN' | 'CLOSED';
  auctionDayOfMonth?: number;
  tdsApplicable?: boolean;
  gstApplicable?: boolean;
  registrarRef?: string;
  startDate?: string;
  commissionRate?: number;
  durationCycles?: number;
};

const schemeDetailConfig = defineDetailPage<SchemeWithFields>({
  resource: 'schemes',
  fetchById: async () => { throw new Error('fetchById not used directly'); },
  tabs: [
    { id: 'Overview', label: 'Overview' },
    { id: 'Subscribers', label: 'Subscribers' },
    { id: 'Cycles', label: 'Cycles' },
    { id: 'Ledger', label: 'Ledger' },
    { id: 'Documents', label: 'Documents' },
    { id: 'Closure', label: 'Closure' },
  ],
});

function SchemeDetail() {
 usePageTitle('Scheme Detail');
  const { id } = Route.useParams();
  const schemeQuery = useScheme(id);
  const { data: cycles } = useSchemeCycles(id);
  const detail = useDetailPage(schemeDetailConfig, id, { enabled: false });

  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [subscribersPage, setSubscribersPage] = useState(1);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<{ id: string; fullName: string; mobile?: string | null; status?: string } | null>(null);
  const [selectedSubscriberId, setSelectedSubscriberId] = useState<string | null>(null);
  const [showDeceasedDialog, setShowDeceasedDialog] = useState(false);
  const [selectedDeceasedSubscriber, setSelectedDeceasedSubscriber] = useState<SubscriberListItem | null>(null);
  const [editingCycle, setEditingCycle] = useState<{ cycleNumber: number; auctionDayOfMonth: number } | null>(null);
  const [newAuctionDay, setNewAuctionDay] = useState<number>(10);
  const updateCycleAuctionDayMutation = useUpdateCycleAuctionDay(id);

  const { toast } = useToast();

  const debouncedCustomerSearch = useDebounce(customerSearch, 300);

  const { data: subscribersResponse, isLoading: subscribersLoading, error: subscribersError } = useSchemeSubscribers(id, { page: subscribersPage, limit: 50 });
  const { data: customerLookupResponse, isLoading: searchingCustomers } = useCustomers({ page: 1, limit: 10, query: debouncedCustomerSearch || undefined });

  const enrollMutation = useEnrollSubscriber(id);
  const admitMutation = useAdmitSubscriber(id);

  const enrollSchema = z.object({
    assignedTickets: z.string().min(1, 'Please assign at least one ticket number.'),
    agreedToTerms: z.literal(true, { message: 'Please agree to the scheme terms.' }),
  });

  const enrollModal = useFormModal(
    { defaultValues: { assignedTickets: '', agreedToTerms: false }, schema: enrollSchema },
    enrollMutation,
    {
      onBeforeSubmit: (value) => {
        if (!selectedCustomer) throw new Error('Please select a customer first.');
        const tickets = value.assignedTickets.split(',').map((t) => t.trim()).filter(Boolean).map((t) => new Decimal(t).toNumber());
        if (tickets.some(isNaN) || tickets.some((t) => t < 1)) throw new Error('Ticket numbers must be positive integers.');
        return { customerId: selectedCustomer.id, ticketNumbers: tickets };
      },
      onSuccess: () => {
        setSelectedCustomer(null);
        setCustomerSearch('');
        setShowEnrollModal(false);
      },
      onError: (err) => {
        toast({ type: 'error', title: 'Error', description: err.message || 'Failed to enroll subscriber.' });
      },
    },
  );

  const subscribersList = subscribersResponse?.data || [];
  const filteredSubscribers = useMemo(() => {
    if (statusFilter === 'ALL') return subscribersList;
    return subscribersList.filter((s) => s.status === statusFilter);
  }, [subscribersList, statusFilter]);


  return (
    <>
      <DataView
      query={schemeQuery}
      loadingSkeleton={
        <DetailPage
          config={schemeDetailConfig}
          detail={{ ...detail, isLoading: true, isError: false, error: null, refetch: () => {} }}
          title="Loading..."
          backButton={<Link to="/schemes" className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary transition-colors w-fit"><ChevronLeft className="h-4 w-4" />Back</Link>}
          mainPane={null}
        />
      }
    >
      {(schemeRaw) => {
        const scheme = schemeRaw as SchemeWithFields;

        const headerActions = (
          <div className="flex items-center gap-3">
            <SchemeStatusActions scheme={scheme} />
            {scheme.status === 'ACTIVE' && (
              <span className="text-sm text-text-secondary">Cycle operations are automated. See Cycles tab.</span>
            )}
          </div>
        );

        const statusBadge = (
          <div className="flex items-center gap-3 mt-1">
            <StatusBadge status={scheme.status === 'ACTIVE' ? 'success' : ['DRAFT', 'PENDING_REGISTRATION'].includes(scheme.status) ? 'warning' : ['SUSPENDED', 'REJECTED'].includes(scheme.status) ? 'danger' : 'info'} label={scheme.status} />
            <span className="text-sm text-text-secondary">Progress: {scheme.currentCycle} / {scheme.durationCycles}</span>
          </div>
        );

        return (
          <DetailPage
            config={schemeDetailConfig}
            detail={{ ...detail, data: scheme, isLoading: false, isError: false, error: null, refetch: schemeQuery.refetch }}
            title={`${scheme.schemeNumber} — ${scheme.displayName}`}
            subtitle={statusBadge}
            backButton={<Link to="/schemes" className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary transition-colors w-fit"><ChevronLeft className="h-4 w-4" />Back</Link>}
            headerActions={headerActions}
            mainPane={
        <div>
          <div className="border-b border-border mb-6">
            <nav className="-mb-px flex space-x-8 items-center">
              {scheme && scheme.id && (
                <Link to="/schemes/$id/commission" params={{ id }} className="border-transparent text-text-secondary hover:text-text-primary hover:border-accent-200 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors">
                  Commissions
                </Link>
              )}
            </nav>
          </div>

      {detail.activeTab === 'Overview' && (
       <Card className="border border-border shadow-sm">
         <CardContent className="p-6 grid grid-cols-2 gap-6 bg-surface-1">
           <div><p className="text-sm text-text-secondary">Chit Value</p><p className="font-mono font-medium text-text-primary"><Money paise={scheme.chitValue} /></p></div>
           <div><p className="text-sm text-text-secondary">Subscribers</p><p className="font-medium text-text-primary">{scheme.subscriberCount}</p></div>
           <div><p className="text-sm text-text-secondary">Cycle Frequency</p><p className="font-medium text-text-primary">{scheme.cycleFrequency}</p></div>
           <div><p className="text-sm text-text-secondary">Commission Rate</p><p className="font-mono font-medium text-text-primary">{((scheme.commissionRate ?? 0) * 100).toFixed(2)}%</p></div>
           <div><p className="text-sm text-text-secondary">Auction Type</p><p className="font-medium text-text-primary">{scheme.auctionType}</p></div>
           <div><p className="text-sm text-text-secondary">TDS Applicable</p><p className="font-medium text-text-primary">{scheme.tdsApplicable ? 'Yes' : 'No'}</p></div>
           <div><p className="text-sm text-text-secondary">Registrar Ref</p><p className="font-medium text-text-primary">{scheme.registrarRef || '—'}</p></div>
            <div><p className="text-sm text-text-secondary">Commencement Date</p><p className="font-medium text-text-primary">{scheme.startDate || '—'}</p></div>
         </CardContent>
       </Card>
     )}

      {detail.activeTab === 'Subscribers' && scheme && (
       <div className="space-y-4">
         <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-surface-2 p-4 rounded-[10px] border border-border">
           <div className="flex flex-wrap items-center gap-2">
             <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider mr-2">Filter:</span>
             {['ALL', 'ACTIVE', 'PRIZED', 'DEFAULTED', 'EXITED', 'SUSPENDED'].map((filter) => (
               <Button key={filter} variant={statusFilter === filter ? 'primary' : 'secondary'} size="sm" onClick={() => setStatusFilter(filter)}>{filter}</Button>
             ))}
           </div>
           <Button onClick={() => setShowEnrollModal(true)} variant="primary" disabled={['CLOSING', 'CLOSED', 'COMPLETED', 'TERMINATED'].includes(scheme.status)}>
             Enroll Customer
           </Button>
         </div>

         <Card className="border border-border shadow-sm bg-surface-1">
           <CardContent className="p-0">
             {subscribersLoading ? (
               <Skeleton rows={5} />
             ) : subscribersError ? (
               <div className="p-8 text-center"><p className="text-sm font-semibold text-danger-600">Failed to load subscribers</p></div>
             ) : filteredSubscribers.length === 0 ? (
               <div className="p-8 text-center text-text-muted text-sm">
                 {statusFilter !== 'ALL' ? `No subscribers found with status ${statusFilter}.` : 'Click "Enroll Customer" to get started.'}
               </div>
             ) : (
               <table className="w-full border-collapse text-left text-sm">
                 <thead className="bg-surface-2 text-text-secondary font-semibold uppercase tracking-wider text-xs border-b border-border">
                   <tr>
                     <th className="p-4">Ticket No</th>
                     <th className="p-4">Customer</th>
                     <th className="p-4">Status</th>
                     <th className="p-4">Prized State</th>
                      <th className="p-4">Joined At</th>
                      <th className="p-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border text-text-primary">
                     {filteredSubscribers.map((s: SubscriberListItem) => (
                      <tr key={s.id} onClick={() => setSelectedSubscriberId(s.id)} className="hover:bg-surface-2 transition-colors cursor-pointer">
                        <td className="p-4 font-mono text-sm">{(s.ticketNumbers || []).map((t: number) => `#${t}`).join(', ')}</td>
                        <td className="p-4">
                          {s.customerId ? (
                            <Link to={`/customers/${s.customerId}` as never} className="font-medium text-accent-500 hover:underline">{s.customerName || 'Unknown'}</Link>
                          ) : <span className="font-medium text-text-primary">{s.customerName || 'Unknown'}</span>}
                         {s.customerMobile && <span className="text-xs text-text-secondary block">{s.customerMobile}</span>}
                       </td>
                       <td className="p-4">
                         <StatusBadge status={s.status === 'ACTIVE' ? 'success' : s.status === 'PRIZED' ? 'warning' : s.status === 'DEFAULTED' ? 'danger' : 'info'} label={s.status} />
                       </td>
                       <td className="p-4">
                         {s.status === 'PRIZED' ? (
                           <span className="text-xs font-semibold text-warning-500 bg-warning-bg px-2 py-0.5 rounded-full">Prized</span>
                         ) : (
                           <span className="text-xs font-semibold text-text-muted bg-surface-2 px-2 py-0.5 rounded-full">Unprized</span>
                         )}
                       </td>
                        <td className="p-4 text-sm text-text-secondary">{s.enrolledAt ? new Date(s.enrolledAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</td>
                        <td className="p-4">
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            {s.tickets?.some((t) => t.status === 'PENDING_ADMISSION') && (
                              <Button
                                variant="primary"
                                size="sm"
                                className="text-xs bg-success-600 hover:bg-success-700 text-white px-2 py-0.5"
                                onClick={() => {
                                  const pendingTicket = s.tickets?.find((t) => t.status === 'PENDING_ADMISSION');
                                  if (pendingTicket) {
                                    admitMutation.mutate(pendingTicket.id, {
                                      onSuccess: () => {
                                        toast({ type: 'success', title: 'Admitted', description: `Ticket #${pendingTicket.ticketNumber} admitted successfully.` });
                                      },
                                      onError: (err: Error) => {
                                        toast({ type: 'error', title: 'Admission Failed', description: err.message || 'Failed to admit subscriber' });
                                      },
                                    });
                                  }
                                }}
                                loading={admitMutation.isPending}
                              >
                                Admit
                              </Button>
                            )}
                            <Link
                              to="/schemes/transfers"
                              search={{ schemeId: id, subscriberId: s.id }}
                              className="text-xs text-accent-500 hover:text-accent-700 px-1.5 py-0.5 inline-flex items-center rounded hover:bg-surface-2 transition-colors"
                            >
                              Transfer
                            </Link>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-xs text-danger-500 hover:text-danger-700 px-1.5 py-0.5"
                              onClick={() => { setSelectedDeceasedSubscriber(s); setShowDeceasedDialog(true); }}
                            >
                              Deceased
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                 </tbody>
               </table>
             )}
             <div className="flex items-center justify-between px-4 py-3 bg-surface-2 border-t border-border text-sm text-text-muted">
               <span>Page {subscribersPage}</span>
               <div className="flex gap-2">
                 <Button variant="secondary" size="sm" disabled={subscribersPage <= 1} onClick={() => setSubscribersPage(p => p - 1)}>Previous</Button>
                 <Button variant="secondary" size="sm" disabled={((subscribersResponse?.meta?.total as number) || 0) <= subscribersPage * 50} onClick={() => setSubscribersPage(p => p + 1)}>Next</Button>
               </div>
             </div>
             
             {showEnrollModal && (
                <FormModal
                  open={showEnrollModal}
                  modal={enrollModal}
                  title="Enroll New Customer"
                  submitLabel="Enroll Subscriber"
                  onClose={() => {
                    setShowEnrollModal(false);
                    setSelectedCustomer(null);
                    setCustomerSearch('');
                  }}
                >
                  {!selectedCustomer ? (
                    <div className="space-y-1.5">
                      <label className="block text-xs font-medium text-text-secondary uppercase tracking-wide">Search Customer</label>
                      <Input placeholder="Search by name, mobile, or ID..." value={customerSearch} onChange={(e) => setCustomerSearch(e.target.value)} className="w-full" />
                      {searchingCustomers && <div className="text-xs text-text-muted">Searching...</div>}
                      <div className="max-h-40 overflow-y-auto divide-y divide-border border border-border rounded-[6px] bg-surface-1 mt-1">
                        {customerLookupResponse?.data?.map((customer) => (
                          <Button key={customer.id} type="button" onClick={() => setSelectedCustomer({ id: customer.id, fullName: customer.name, mobile: customer.mobile, status: customer.status })}
                            className="w-full text-left px-3 py-2 text-sm text-text-primary hover:bg-surface-2 transition-colors flex justify-between"
                          >
                            <span className="font-medium">{customer.name} {customer.status === 'BLACKLISTED' && <span className="text-danger-500 text-xs ml-2 font-bold">(BLACKLISTED)</span>}</span>
                            <span className="text-text-secondary text-xs">{customer.mobile}</span>
                          </Button>
                        ))}
                        {customerSearch && !searchingCustomers && customerLookupResponse?.data?.length === 0 && (
                          <div className="p-3 text-sm text-text-muted text-center">No customers found.</div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="bg-surface-2 p-3 rounded-[6px] border border-border flex justify-between items-center">
                        <div><p className="text-sm font-semibold text-text-primary">{selectedCustomer.fullName}</p><p className="text-xs text-text-secondary">Mobile: {selectedCustomer.mobile}</p></div>
                        <Button type="button" variant="secondary" size="sm" onClick={() => setSelectedCustomer(null)}>Change</Button>
                      </div>
                      {selectedCustomer.status === 'BLACKLISTED' && (
                        <div className="p-3 bg-danger-50 text-danger-700 border border-danger-200 rounded-[6px] text-sm">
                          ⚠️ This customer is blacklisted. Enrollment will be blocked.
                        </div>
                      )}
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-medium text-text-secondary uppercase tracking-wide">Assign Ticket Number(s)</label>
                    <enrollModal.form.Field name="assignedTickets" children={(field: { state: { value: string; meta: unknown }; name: string; handleBlur: () => void; handleChange: (val: string) => void }) => (
                      <>
                        <Input placeholder="e.g. 12, 13" value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} className="w-full" />
                        {enrollModal.fieldError(field.state.meta) && <p className="text-danger-text text-xs">{enrollModal.fieldError(field.state.meta)}</p>}
                      </>
                    )} />
                    <p className="text-xs text-text-muted">Comma-separated integers only (e.g. "1, 2").</p>
                  </div>
                  <div className="space-y-2 bg-surface-2 p-3 rounded-[6px] border border-border">
                    <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Enrollment Terms</p>
                    <div className="grid grid-cols-2 gap-3 text-xs leading-relaxed text-text-primary">
                      <div><span className="text-text-muted block text-caption uppercase font-semibold">Chit Value</span><span className="font-medium"><Money paise={scheme.chitValue} /></span></div>
                      <div><span className="text-text-muted block text-caption uppercase font-semibold">Installment Amount</span><span className="font-medium"><Money paise={new Decimal(scheme.chitValue).div(scheme.durationCycles).toNumber()} /></span></div>
                      <div><span className="text-text-muted block text-caption uppercase font-semibold">Duration</span><span className="font-medium">{scheme.durationCycles} Cycles</span></div>
                      <div><span className="text-text-muted block text-caption uppercase font-semibold">Commission Rate</span><span className="font-medium">{((scheme.commissionRate ?? 0) * 100).toFixed(2)}%</span></div>
                    </div>
                    <div className="flex items-start gap-2 pt-2 border-t border-border mt-2">
                      <enrollModal.form.Field name="agreedToTerms" children={(field: { state: { value: boolean; meta: unknown }; name: string; handleBlur: () => void; handleChange: (val: boolean) => void }) => (
                        <Input id="agree-checkbox" type="checkbox" checked={field.state.value} onChange={(e) => field.handleChange(e.target.checked)} className="mt-1 accent-accent-500" />
                      )} />
                      <label htmlFor="agree-checkbox" className="text-xs text-text-secondary select-none leading-relaxed">I agree to the rules and terms of this scheme. I understand that the KYC snapshot will be captured in V2 automatically.</label>
                    </div>
                  </div>
                  <enrollModal.form.Subscribe selector={(state: { errorMap: { onSubmit: unknown } }) => state.errorMap.onSubmit} children={(error: unknown) =>
                    error ? <div className="p-3 bg-danger-bg text-danger-text text-xs rounded-[6px] border border-danger-500 leading-relaxed font-medium">{String(error)}</div> : null
                  } />
                </FormModal>
             )}

              <MemberDetailDrawer
                schemeId={id}
                subscriberId={selectedSubscriberId}
                onClose={() => setSelectedSubscriberId(null)}
              />

              {selectedDeceasedSubscriber && (
                <MarkDeceasedDialog
                  isOpen={showDeceasedDialog}
                  onClose={() => { setShowDeceasedDialog(false); setSelectedDeceasedSubscriber(null); }}
                  schemeId={id}
                  subscriberId={selectedDeceasedSubscriber.id}
                  customerName={selectedDeceasedSubscriber.customerName}
                />
              )}
           </CardContent>
         </Card>
       </div>
     )}

      {detail.activeTab === 'Cycles' && scheme && (
       <Card className="border border-border shadow-sm overflow-hidden">
         <CardContent className="p-0">
           <table className="min-w-full divide-y divide-border">
             <thead className="bg-surface-2">
               <tr>
                 <th className="px-6 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Cycle #</th>
                 <th className="px-6 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Scheduled Date</th>
                 <th className="px-6 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Auction Day</th>
                 <th className="px-6 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Status</th>
                 <th className="px-6 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Auction Winner</th>
                 <th className="px-6 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Total Collected</th>
                 <th className="px-6 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Actions</th>
               </tr>
             </thead>
             <tbody className="bg-surface-1 divide-y divide-border">
                {cycles?.map((cycle: { id: string; cycleNumber: number; scheduledDate: string; status: string; auctionWinner: string | null; totalCollected: number | null; auctionId: string | null; auctionDayOfMonth?: number }) => (
                 <tr key={cycle.id} className="hover:bg-surface-2 transition-colors">
                   <td className="px-6 py-4 whitespace-nowrap">
                     <Link to="/schemes/$id/cycles/$num/dues" params={{ id, num: String(cycle.cycleNumber) }} className="text-accent-700 hover:text-accent-500 font-medium hover:underline">
                       {cycle.cycleNumber}
                     </Link>
                   </td>
                   <td className="px-6 py-4 whitespace-nowrap text-text-primary text-sm">{cycle.scheduledDate}</td>
                   <td className="px-6 py-4 whitespace-nowrap text-text-primary text-sm">
                     <div className="flex items-center gap-2">
                       <span>{cycle.auctionDayOfMonth || '—'}</span>
                       {['SCHEDULED', 'OPEN', 'AUCTION_PENDING'].includes(cycle.status) && (
                         <Button
                           variant="ghost"
                           size="sm"
                           className="h-6 w-6 p-0 hover:bg-surface-4 rounded"
                           onClick={(e) => {
                             e.stopPropagation();
                             setEditingCycle({ cycleNumber: cycle.cycleNumber, auctionDayOfMonth: cycle.auctionDayOfMonth || 10 });
                             setNewAuctionDay(cycle.auctionDayOfMonth || 10);
                           }}
                         >
                           <Edit2 className="h-3.5 w-3.5 text-text-secondary" />
                         </Button>
                       )}
                     </div>
                   </td>
                   <td className="px-6 py-4 whitespace-nowrap">
                     <StatusBadge status={cycle.status === 'CLOSED' ? 'success' : cycle.status === 'OPEN' || cycle.status === 'AUCTION_PENDING' || cycle.status === 'AUCTION_DONE' ? 'warning' : 'info'} label={cycle.status} />
                   </td>
                   <td className="px-6 py-4 whitespace-nowrap text-text-primary text-sm">{cycle.auctionWinner || '—'}</td>
                   <td className="px-6 py-4 whitespace-nowrap text-text-primary font-mono text-sm">{cycle.totalCollected ? <Money paise={cycle.totalCollected} /> : '—'}</td>
                   <td className="px-6 py-4 whitespace-nowrap">
                     <Link to="/schemes/$id/cycles/$num/auction" params={{ id, num: String(cycle.cycleNumber) }} className="text-accent-700 hover:text-accent-500 hover:underline text-sm font-medium transition-colors">
                       {cycle.auctionId ? 'View Auction' : 'Run Auction'}
                     </Link>
                   </td>
                 </tr>
               ))}
               {!cycles?.length && (
                 <tr><td colSpan={7} className="px-6 py-4 text-center text-text-secondary text-sm bg-surface-1">No cycles generated yet.</td></tr>
               )}
             </tbody>
           </table>
         </CardContent>
       </Card>
     )}

{detail.activeTab === 'Ledger' && (
        <SchemeLedgerTab schemeId={id} />
      )}

{detail.activeTab === 'Documents' && (
        <SchemeDocumentsTab schemeId={id} />
      )}

      {detail.activeTab === 'Closure' && (
        <SchemeClosureTab schemeId={id} schemeStatus={scheme.status} />
      )}
        </div>
      }
    />
        );
      }}
    </DataView>

    {editingCycle && (
      <Dialog open={!!editingCycle} onOpenChange={(open) => !open && setEditingCycle(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Cycle #{editingCycle.cycleNumber} Auction Day</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <FormField label="Auction Day of Month (1-28)" required>
              <Input
                type="number"
                min="1"
                max="28"
                value={newAuctionDay}
                onChange={(e) => setNewAuctionDay(new Decimal(e.target.value).toNumber())}
              />
            </FormField>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setEditingCycle(null)}>Cancel</Button>
            <Button 
              variant="primary" 
              onClick={async () => {
                if (!editingCycle) return;
                try {
                  await updateCycleAuctionDayMutation.mutateAsync({
                    cycleNumber: editingCycle.cycleNumber,
                    auctionDayOfMonth: newAuctionDay,
                  });
                  toast({ type: 'success', title: 'Success', description: 'Cycle auction day updated successfully.' });
                  setEditingCycle(null);
                  schemeQuery.refetch();
                } catch (err: unknown) {
                  const message = err instanceof Error ? err.message : 'Failed to update cycle auction day.';
                  console.error('Failed to update cycle auction day:', err);
                  toast({ type: 'error', title: 'Error', description: message });
                }
              }} 
              disabled={updateCycleAuctionDayMutation.isPending}
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )}
  </>
  );
}
